import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { companyApi } from '@/api';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  ErrorState,
  Icon,
  LinkButton,
  LoadingState,
  PageHeader,
  SegmentedTabs,
  useToast,
} from '@/components/ui';
import { PermissionChecklist } from '@/features/access/PermissionChecklist';
import { ACCESS_KEYS, roleLabel, sameSet } from '@/features/access/roles';
import { errorMessage } from '@/hooks/useApiError';
import { usePermissions } from '@/hooks/usePermissions';
import { paths } from '@/routes/paths';
import { PERMISSIONS } from '@/types/roles';
import { cn } from '@/utils/cn';

/**
 * What each workspace role grants in this company. A change reaches every
 * member with the role on their next request, except where a member was
 * individually given or denied a permission on the Team page.
 */
export function CompanyRolesPage() {
  const { can } = usePermissions();
  const allowed = can(PERMISSIONS.COMPANY_MEMBERS_MANAGE);
  const [activeRole, setActiveRole] = useState('HR');

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ACCESS_KEYS.roles,
    queryFn: companyApi.listRoles,
    enabled: allowed,
  });

  const header = (
    <PageHeader
      title="Roles and permissions"
      description="Choose what each role can do in your workspace. Fine-tune individual people from the Team page."
      action={
        <LinkButton to={paths.company.team} variant="secondary">
          <Icon name="team" size={16} />
          Back to team
        </LinkButton>
      }
    />
  );

  if (!allowed) {
    return (
      <div>
        {header}
        <Alert tone="info" title="Only admins can change roles">
          Ask a workspace admin if you need different access. You can see your own permissions under
          My account.
        </Alert>
      </div>
    );
  }
  if (isPending) return <LoadingState label="Loading roles…" />;
  if (isError) {
    return <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />;
  }

  const role = data.roles.find((item) => item.role === activeRole) ?? data.roles[0];

  return (
    <div>
      {header}
      <SegmentedTabs
        label="Role"
        value={role.role}
        onChange={setActiveRole}
        tabs={data.roles.map((item) => ({
          key: item.role,
          label: roleLabel(item.role),
          count: item.member_count,
        }))}
      />
      {/* Keyed so switching tabs, or a save, starts from what is stored. */}
      <RoleEditor
        key={`${role.role}:${role.permissions.join(',')}`}
        role={role}
        catalogue={data.catalogue}
      />
    </div>
  );
}

function RoleEditor({ role, catalogue }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const saved = new Set(role.permissions);
  const defaults = new Set(role.default_permissions);
  const [selected, setSelected] = useState(saved);
  const [confirmReset, setConfirmReset] = useState(false);

  function onSaved(roles, message) {
    queryClient.setQueryData(ACCESS_KEYS.roles, roles);
    // Every member's effective set may have moved.
    void queryClient.invalidateQueries({ queryKey: ['company-members'] });
    void queryClient.invalidateQueries({ queryKey: ['company-member-permissions'] });
    toast.success(message);
  }

  const save = useMutation({
    mutationFn: (permissions) => companyApi.updateRolePermissions(role.role, permissions),
    onSuccess: (roles) => onSaved(roles, `${roleLabel(role.role)} role updated`),
    onError: (saveError) => toast.error(errorMessage(saveError, 'Could not save the role')),
  });

  const reset = useMutation({
    mutationFn: () => companyApi.resetRolePermissions(role.role),
    onSuccess: (roles) => {
      setConfirmReset(false);
      onSaved(roles, `${roleLabel(role.role)} role reset to the default`);
    },
    onError: (resetError) => {
      setConfirmReset(false);
      toast.error(errorMessage(resetError, 'Could not reset the role'));
    },
  });

  function toggle(code) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }

  const allCodes = catalogue.map((entry) => entry.code);
  const selectedCount = allCodes.filter((code) => selected.has(code)).length;
  const allSelected = selectedCount === allCodes.length;
  const someSelected = selectedCount > 0 && !allSelected;

  function toggleAll() {
    setSelected(allSelected ? new Set() : new Set(allCodes));
  }

  const unchanged = sameSet(selected, saved);
  const busy = save.isPending || reset.isPending;
  const memberText = role.member_count === 1 ? '1 member has' : `${role.member_count} members have`;

  return (
    <Card>
      <CardHeader
        title={
          <span className="inline-flex flex-wrap items-center gap-2">
            {roleLabel(role.role)}
            {role.locked && <Badge tone="brand">Full access</Badge>}
            {role.is_customised && <Badge tone="warning">Customised</Badge>}
          </span>
        }
        description={`${memberText} this role. Compared with the platform default below.`}
      />

      {role.locked ? (
        <Alert tone="info" title="The admin role cannot be changed" className="mb-5">
          Admins always hold every permission, including managing the team, so your workspace can
          never be left without someone able to fix access.
        </Alert>
      ) : (
        !unchanged && (
          <Alert tone="warning" className="mb-5">
            Unsaved changes. Saving applies them to everyone with this role straight away.
          </Alert>
        )
      )}

      {!role.locked && (
        <SelectAll
          checked={allSelected}
          indeterminate={someSelected}
          count={selectedCount}
          total={allCodes.length}
          onChange={toggleAll}
          disabled={busy}
        />
      )}

      <PermissionChecklist
        catalogue={catalogue}
        selected={role.locked ? defaults : selected}
        baseline={defaults}
        onToggle={toggle}
        disabled={role.locked || busy}
      />

      {!role.locked && (
        <div className="border-line mt-6 flex flex-col-reverse gap-2 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
          <Button
            variant="ghost"
            type="button"
            disabled={!role.is_customised || busy}
            onClick={() => setConfirmReset(true)}
          >
            <Icon name="reset" size={16} />
            Reset to default
          </Button>
          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            <Button
              variant="secondary"
              type="button"
              disabled={unchanged || busy}
              onClick={() => setSelected(saved)}
            >
              Discard
            </Button>
            <Button
              type="button"
              disabled={unchanged}
              isLoading={save.isPending}
              onClick={() => save.mutate([...selected])}
            >
              Save role
            </Button>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmReset}
        title={`Reset the ${roleLabel(role.role)} role?`}
        message="Everyone with this role goes back to the platform default. Permissions given to or taken from individual people stay as they are."
        confirmLabel="Reset"
        tone="primary"
        isLoading={reset.isPending}
        onConfirm={() => reset.mutate()}
        onCancel={() => setConfirmReset(false)}
      />
    </Card>
  );
}

/**
 * One box over the whole checklist: ticks every permission, or clears them
 * all when everything is already ticked. Shows a dash when only some are.
 * Nothing is saved until "Save role", like any other change here.
 */
function SelectAll({ checked, indeterminate, count, total, onChange, disabled }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <label
      className={cn(
        'border-line bg-surface-muted mb-4 flex items-center gap-2.5 rounded-lg border px-3 py-2 text-sm',
        disabled ? 'cursor-not-allowed opacity-70' : 'hover:border-brand-300 cursor-pointer',
      )}
    >
      <input
        ref={ref}
        type="checkbox"
        className="border-line text-brand-600 focus:ring-brand-500 size-4 shrink-0 rounded border"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
      />
      <span className="text-ink font-semibold">Select all permissions</span>
      <span className="text-ink-muted ml-auto text-xs">
        {count} of {total} selected
      </span>
    </label>
  );
}
